import { AxisCode, MessageType } from '@atlas/types'
import type { UpdateWorkOrderInput, WorkOrder, WorkOrderPack } from '@/domain/work-order.types'

/** The pack of an order nobody has written one for. */
export const EMPTY_PACK: WorkOrderPack = {
	sharepointFolderUrl: null,
	exactDimensions: null,
	dimensionUnit: null,
	mainCopy: null,
	legalContentApplies: null,
	mainCopyApplies: null,
	legalTextApplies: null,
	copyrightApplies: null,
	aiLegalTextApplies: null,
	isiApplies: null,
	piApplies: null,
	updatedAt: null,
}

/** The order's tuple with its channel replaced by the platform, or dropped for none. */
function withChannel(tuple: WorkOrder['tuple'], platform: string | null): WorkOrder['tuple'] {
	const { [AxisCode.CHANNEL]: _channel, ...rest } = tuple ?? {}
	return platform ? { ...rest, [AxisCode.CHANNEL]: [platform] } : rest
}

/** The stored pack with a patch applied: an absent field is left alone, `null` clears it. */
function withPackFields(pack: WorkOrderPack, patch: NonNullable<UpdateWorkOrderInput['pack']>): WorkOrderPack {
	const { mainCopy, ...fields } = patch
	const set = Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined))
	const next: WorkOrderPack = { ...pack, ...set }
	if (mainCopy === undefined) return next
	// A new main copy has no message yet; the server answers with the id it creates.
	return {
		...next,
		mainCopy: mainCopy?.content.trim()
			? { messageId: pack.mainCopy?.messageId ?? '', content: mainCopy.content, messageType: mainCopy.messageType }
			: null,
	}
}

/**
 * The order as a PATCH carrying the pack would leave it: the aspect ratio on the order, the
 * platform as its tuple's channel, the pack fields merged into the stored pack. The optimistic
 * cache write and the mock API both read a pack patch through it, so neither spreads `platform`
 * or a partial `pack` over the order.
 */
export function applyPackPatch(workOrder: WorkOrder, patch: UpdateWorkOrderInput): WorkOrder {
	const { aspectRatio, platform, pack } = patch
	let next = workOrder
	if (aspectRatio !== undefined) next = { ...next, aspectRatio }
	if (platform !== undefined) next = { ...next, tuple: withChannel(next.tuple, platform) }
	if (pack !== undefined) next = { ...next, pack: withPackFields(next.pack ?? EMPTY_PACK, pack) }
	return next
}

/** The message type a main copy is saved as: the one it already has, Body for a new one. */
export const mainCopyTypeOf = (pack: WorkOrderPack | undefined): MessageType =>
	pack?.mainCopy?.messageType ?? MessageType.BODY
